import { ColumnDef } from '@/components/data-table/DataTable';
import { Avatar } from '@/components/ui/Avatar';
import {
	CONTRACT_TYPE_TRANSLATION,
	SERVICE_MODALITY_TRANSLATIONS,
} from '@/constants/professional/translations';
import { Plus, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatProfessionalFullName } from '@/utils';

interface ProfessionalColumnsSessionProps {
	onSelect: (professional: Professionals.Professional) => void;
	/** ID do profissional selecionado; quando igual ao da linha, o botão mostra Check */
	selectedProfessionalId?: string | null;
}

const ProfessionalColumnsSession = ({
	onSelect,
	selectedProfessionalId,
}: ProfessionalColumnsSessionProps): ColumnDef<
	Professionals.Professional,
	any
>[] => {
	return [
		{
			header: 'Profissional',
			accessor: 'first_name',
			cell: (_, row) => (
				<div className="flex items-center">
					{row.profile_photo_url && (
						<Avatar
							className="mr-3 h-8 w-8"
							variant="circle"
							img_url={row.profile_photo_url}
						/>
					)}
					<div className="truncate">
						<p className="truncate font-medium text-dark">
							{formatProfessionalFullName(row)}
						</p>
						<p className="truncate text-xs text-gray-500">
							{row.email || 'N/A'}
						</p>
					</div>
				</div>
			),
		},
		{
			header: 'Status',
			accessor: 'banned',
			cell: (value) => (
				<span
					className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
						value
							? 'bg-red-100 text-red-800'
							: 'bg-green-100 text-green-800'
					}`}
				>
					{value ? 'Desativado' : 'Ativo'}
				</span>
			),
		},
		{
			header: 'Modalidade',
			accessor: 'service_modality',
			cell: (value) => {
				return (
					<span className="text-gray-700">
						{
							SERVICE_MODALITY_TRANSLATIONS[
								value as Professionals.ServiceModality
							]
						}
					</span>
				);
			},
		},
		{
			header: 'Clínica/Endereço',
			accessor: 'clinic',
			cell: (_, row) => {
				const modality =
					row.service_modality as Professionals.ServiceModality;
				if (modality === 'FULL_REMOTE') {
					return <span className="text-dark">Online</span>;
				}
				if (row.clinic?.address) {
					return (
						<span className="text-dark">
							Presencial (Endereço Disponível)
						</span>
					);
				}

				return (
					<span className="text-dark">
						Presencial (Endereço Não Informado)
					</span>
				);
			},
		},
		{
			header: 'Serviço',
			accessor: 'professional_services',
			cell: (_, row) => {
				const serviceName =
					row.professional_services?.[0]?.service?.service_name ||
					'Não Vinculado';
				return <span className="text-gray-700">{serviceName}</span>;
			},
		},
		{
			header: 'Contrato',
			accessor: 'contract',
			cell: (value) => {
				const contractValue = value as Professionals.Contract;
				return (
					<span
						className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
							contractValue === 'PARTNER'
								? 'bg-purple-100 text-purple-800'
								: 'bg-yellow-100 text-yellow-800'
						}`}
					>
						{CONTRACT_TYPE_TRANSLATION[contractValue]}
					</span>
				);
			},
		},
		{
			header: 'Selecionar',
			accessor: 'id',
			cell: (_, row) => {
				const isSelected =
					selectedProfessionalId != null &&
					row.id === selectedProfessionalId;
				return (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onSelect(row);
						}}
						className={cn(
							'flex h-9 w-9 items-center justify-center rounded-xl border border-solid outline-none transition focus:outline-none',
							isSelected
								? 'border-zinc-700 bg-zinc-700 text-white shadow-sm hover:border-zinc-800 hover:bg-zinc-800'
								: 'border-stone-300 bg-white text-zinc-600 hover:border-stone-400 hover:bg-stone-50',
						)}
					>
						{isSelected ? (
							<Check size={20} strokeWidth={3} />
						) : (
							<Plus size={20} strokeWidth={2.5} />
						)}
						<span className="sr-only">
							{isSelected ? 'Selecionado' : 'Selecionar'}
						</span>
					</button>
				);
			},
		},
	];
};

export { ProfessionalColumnsSession };
